import { useState, useEffect } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sparkles, Loader2, Check, AlertTriangle, BookOpen } from 'lucide-react';

const OBJECTIVES = [
  ['awareness', 'Reconocimiento'], ['engagement', 'Interacción'], ['leads', 'Captar lead'],
  ['sales', 'Venta'], ['traffic', 'Tráfico'], ['education', 'Educación'],
];
const VARIANT_LABELS = { conservadora: 'Conservadora', persuasiva: 'Persuasiva', creativa: 'Creativa' };

/**
 * Fase 3 — Estudio de Copy con IA. Llama al gateway real (rate limiter +
 * entitlements + AIRequestLog vía backend) y al RAG de la Biblioteca —
 * nunca genera texto localmente ni simula una respuesta.
 */
export default function AiCopyStudio({ network, onApply }) {
  const [topic, setTopic] = useState('');
  const [objective, setObjective] = useState('');
  const [product, setProduct] = useState('');
  const [audience, setAudience] = useState('');
  const [tone, setTone] = useState('');
  const [mustInclude, setMustInclude] = useState('');
  const [forbidden, setForbidden] = useState('');
  const [methodologyKey, setMethodologyKey] = useState('');
  const [useKnowledge, setUseKnowledge] = useState(true);
  const [methodologies, setMethodologies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get('/tenant/social-studio/methodologies').then((r) => setMethodologies(r.data.methodologies || [])).catch(() => {});
  }, []);

  const generate = async () => {
    if (!topic.trim()) { toast.error('Escribe un tema o idea'); return; }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post('/tenant/social-studio/copy/generate', {
        topic, objective: objective || null, product: product || null, audience: audience || null,
        tone: tone || null, network, must_include: mustInclude || null, forbidden: forbidden || null,
        methodology_key: methodologyKey || null, use_knowledge: useKnowledge,
      });
      setResult(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="ai-copy-studio">
      <div className="grid grid-cols-2 gap-2">
        <div className="col-span-2">
          <Label className="text-xs">Tema o idea</Label>
          <Textarea value={topic} onChange={(e) => setTopic(e.target.value)} rows={2} placeholder="Ej: promoción de fin de mes en cortes de cabello" data-testid="copy-topic" />
        </div>
        <div>
          <Label className="text-xs">Objetivo</Label>
          <Select value={objective} onValueChange={setObjective}>
            <SelectTrigger><SelectValue placeholder="Elegir" /></SelectTrigger>
            <SelectContent>{OBJECTIVES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Tono</Label>
          <Input value={tone} onChange={(e) => setTone(e.target.value)} placeholder="cercano, profesional..." />
        </div>
        <div>
          <Label className="text-xs">Producto/oferta</Label>
          <Input value={product} onChange={(e) => setProduct(e.target.value)} />
        </div>
        <div>
          <Label className="text-xs">Audiencia</Label>
          <Input value={audience} onChange={(e) => setAudience(e.target.value)} />
        </div>
        <div>
          <Label className="text-xs">Debe incluir</Label>
          <Input value={mustInclude} onChange={(e) => setMustInclude(e.target.value)} />
        </div>
        <div>
          <Label className="text-xs">Prohibido</Label>
          <Input value={forbidden} onChange={(e) => setForbidden(e.target.value)} />
        </div>
        <div className="col-span-2">
          <Label className="text-xs">Metodología</Label>
          <Select value={methodologyKey} onValueChange={setMethodologyKey}>
            <SelectTrigger><SelectValue placeholder="Sin metodología específica" /></SelectTrigger>
            <SelectContent>
              {methodologies.map((m) => (
                <SelectItem key={m.key} value={m.key} disabled={!m.usable}>
                  {m.name}{!m.usable ? ' (pendiente de material autorizado)' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex items-center justify-between border border-[#E2E8F0] rounded-lg px-3 py-2 bg-[#F8FAFC]">
        <div className="flex items-center gap-2">
          <BookOpen className="w-3.5 h-3.5 text-[#64748B]" />
          <span className="text-xs text-[#0F172A]">Usar mi Biblioteca de Conocimiento</span>
        </div>
        <Switch checked={useKnowledge} onCheckedChange={setUseKnowledge} />
      </div>

      <Button onClick={generate} disabled={loading} className="w-full gap-2" data-testid="copy-generate-button">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Generar 3 variantes
      </Button>

      {result?.warnings?.length > 0 && (
        <div className="space-y-1">
          {result.warnings.map((w, i) => (
            <div key={i} className="flex items-start gap-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
              <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" /> {w}
            </div>
          ))}
        </div>
      )}

      {result?.variants?.map((v, i) => (
        <div key={i} className="border border-[#E2E8F0] rounded-lg p-3 space-y-2" data-testid={`copy-variant-${v.variant}`}>
          <div className="flex items-center justify-between flex-wrap gap-1">
            <span className="text-xs font-semibold text-[#F72585]">{VARIANT_LABELS[v.variant] || v.variant}</span>
            <div className="flex gap-1">
              <Button size="sm" variant="outline" className="h-6 text-[10px] gap-1" onClick={() => onApply(v, 'global')}>
                <Check className="w-3 h-3" /> Aplicar
              </Button>
              <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => onApply(v, 'facebook')}>Solo FB</Button>
              <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => onApply(v, 'instagram')}>Solo IG</Button>
            </div>
          </div>
          <p className="text-sm font-medium text-[#0F172A]">{v.hook}</p>
          <p className="text-xs text-[#334155] whitespace-pre-wrap">{v.body}</p>
          <p className="text-xs text-[#64748B] italic">{v.cta}</p>
          {v.hashtags?.length > 0 && (
            <p className="text-[11px] text-[#94A3B8]">{v.hashtags.map((h) => `#${h}`).join(' ')}</p>
          )}
          {v.compliance_warnings?.length > 0 && (
            <div className="space-y-1">
              {v.compliance_warnings.map((w, j) => (
                <div key={j} className="flex items-start gap-1 text-[10px] text-amber-700"><AlertTriangle className="w-2.5 h-2.5 mt-0.5 flex-shrink-0" /> {w}</div>
              ))}
            </div>
          )}
        </div>
      ))}

      {result?.knowledge_sources?.length > 0 && (
        <div className="text-[10px] text-[#94A3B8]">
          Fuentes de Biblioteca usadas: {result.knowledge_sources.map((s) => s.title).join(', ')}
        </div>
      )}
    </div>
  );
}
